import type { Car } from "@views/Upholstery/types"
import { getApplications, isProductActive, unwrapValue } from "./normalizePdm"

export type PdmVehicleCatalogStats = {
  product_count: number
  active_products: number
  applications_scanned: number
  catalog_groups: number
}

export type PdmVehicleCatalogResult = {
  catalog: Car[]
  stats: PdmVehicleCatalogStats
}

/** Expand `applications[].years` into individual year strings. */
export function expandApplicationYears(yearsField: unknown): string[] {
  if (yearsField === null || yearsField === undefined) {
    return []
  }
  if (Array.isArray(yearsField)) {
    return yearsField
      .map((y) => String(y).trim())
      .filter((y) => y !== "" && /^\d{4}$/.test(y))
  }
  if (typeof yearsField === "string") {
    const s = yearsField.trim()
    if (/^\d{4}-\d{4}$/.test(s)) {
      const [from, to] = s.split("-").map((x) => Number(x))
      const out: string[] = []
      for (let y = from; y <= to; y++) {
        out.push(String(y))
      }
      return out
    }
    return s
      .split(/[\s,/]+/)
      .map((p) => p.trim())
      .filter((p) => p !== "" && /^\d{4}$/.test(p))
  }
  const n = Number(yearsField)
  if (!Number.isNaN(n) && n >= 1000 && n <= 9999) {
    return [String(n)]
  }
  return []
}

function toPickerCar(make: string, model: string, years: string[]): Car {
  const sortedYears = [...years].sort((a, b) => Number(a) - Number(b))
  return {
    id: `pdm:${make}:${model}`,
    make,
    model,
    years: sortedYears.join(", "),
    trims: "",
    driverFactorySeatType: "",
    passengerFactorySeatType: "",
    image: null as unknown as Car["image"],
  }
}

/**
 * Scan active PDM products and build a make/model/year picker catalog
 * grouped by make+model (years comma-separated), matching Dato `Car` shape.
 */
export function deriveVehicleCatalogFromPdm(
  products: Record<string, unknown>[]
): PdmVehicleCatalogResult {
  const grouped = new Map<
    string,
    { make: string; model: string; years: Set<string> }
  >()
  let applicationsScanned = 0
  let activeProducts = 0

  for (const product of products) {
    if (!isProductActive(product)) {
      continue
    }
    activeProducts++

    for (const app of getApplications(product)) {
      applicationsScanned++
      const make = unwrapValue(app.make ?? app.Make)
      const model = unwrapValue(app.model ?? app.Model)
      if (!make || !model) {
        continue
      }

      const years = expandApplicationYears(app.years ?? app.Years)
      if (years.length === 0) {
        continue
      }

      const key = `${make.toLowerCase()}|${model.toLowerCase()}`
      if (!grouped.has(key)) {
        grouped.set(key, { make, model, years: new Set() })
      }
      for (const year of years) {
        grouped.get(key)!.years.add(year)
      }
    }
  }

  const catalog = Array.from(grouped.values())
    .map((g) => toPickerCar(g.make, g.model, [...g.years]))
    .sort(
      (a, b) => a.make.localeCompare(b.make) || a.model.localeCompare(b.model)
    )

  return {
    catalog,
    stats: {
      product_count: products.length,
      active_products: activeProducts,
      applications_scanned: applicationsScanned,
      catalog_groups: catalog.length,
    },
  }
}
